import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormGroupDirective,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { catchError, debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import { EMPTY, first, merge, Observable, of } from 'rxjs';
import { GeocoderService } from '@core/services/geocoder/geocoder.service';
import { Location } from '@data/matching-session/matching-session.model';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { FlexModule } from '@ngbracket/ngx-layout';
import { MatInput } from '@angular/material/input';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

const MIN_QUERY_LENGTH = 3;

@Component({
  selector: 'recrewt-location-selector',
  templateUrl: './location-selector.component.html',
  styleUrls: ['./location-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormField,
    FlexModule,
    MatInput,
    MatAutocompleteTrigger,
    FormsModule,
    MatIcon,
    MatSuffix,
    MatError,
    MatLabel,
    MatAutocomplete,
    MatOption,
    AsyncPipe,
    TranslateModule,
  ],
})
export class LocationSelectorComponent implements OnInit {
  private rootFormGroup = inject(FormGroupDirective);

  private locationService = inject(GeocoderService);

  private cdr = inject(ChangeDetectorRef);

  private destroyRef = inject(DestroyRef);

  form!: FormGroup;

  readonly controlName = input.required<string>();

  readonly error = input<string | undefined>('LOCATION_SELECT.error');

  readonly require = input(false);

  readonly disableFloatLabel = input(false);

  readonly type = input<'address' | 'place'>('address');

  readonly searchControl = new FormControl('', { nonNullable: true });

  initValue?: Location;

  /** One declarative stream: debounced searches merged with the selected form value. */
  results$: Observable<Location[]> = of([]);

  displayValue = (value: Location) => value?.address ?? '';

  street = (value: Location) => value?.address?.split(',')[0]?.trim() ?? '';

  city = (value: Location) => value?.address?.split(',')[1]?.trim() ?? '';

  ngOnInit(): void {
    this.form = this.rootFormGroup.control as FormGroup;
    const control = this.form.get(this.controlName());

    const searchResults$ = this.searchControl.valueChanges.pipe(
      debounceTime(500),
      map((term) => term?.trim() ?? ''),
      filter((term) => term.length >= MIN_QUERY_LENGTH),
      switchMap((term) => this.searchLocations(term)),
    );

    const selectedValue$ = (control?.valueChanges ?? EMPTY).pipe(
      filter((value): value is Location => value != null && typeof value === 'object'),
      tap((value) => (this.initValue = value)),
      map((value) => [value]),
    );

    this.results$ = merge(searchResults$, selectedValue$);

    this.restoreFromQuery(control);
  }

  setFormFieldValue(value: Location) {
    this.form.get(this.controlName())?.setValue(value);
    this.cdr.markForCheck();
  }

  /** Restores a plain-string form value (e.g. from a URL query) to a geocoded Location. */
  private restoreFromQuery(control: AbstractControl | null): void {
    const initValue: unknown = control?.value;
    if (typeof initValue !== 'string' || !initValue.trim()) {
      return;
    }
    this.searchLocations(initValue.trim())
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe((results) => {
        this.initValue = results?.[0];
        if (results?.[0]) {
          this.setFormFieldValue(results[0]);
        }
        this.cdr.markForCheck();
      });
  }

  /** One failed request must not kill the autocomplete — degrade to an empty result. */
  private searchLocations(term: string): Observable<Location[]> {
    return this.locationService
      .searchAddress(term.toLowerCase(), this.type())
      .pipe(catchError(() => of([] as Location[])));
  }
}
